import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Activity,
  AlertTriangle,
  BarChart3,
  Clock3,
  Download,
  FileSpreadsheet,
  Eye,
  RefreshCw,
  ShieldCheck,
  TrendingUp,
} from "lucide-react";
import LoadingSpinner from "../../common/LoadingSpinner";
import { supervisorAPI } from "../../../services/api";
import { useProjectContext } from "../../../hooks/useProjectContext";

const hasMetric = (value) =>
  value !== null && value !== undefined && Number.isFinite(Number(value));

const pct = (value) => (hasMetric(value) ? `${Number(value).toFixed(1)}%` : "N/A");

const hoursText = (value, sampleCount) => {
  if (Number(sampleCount || 0) <= 0 || !hasMetric(value)) return "—";
  const hours = Number(value);
  return hours < 0.1 ? "<0.1h" : `${hours.toFixed(1)}h`;
};

const exceptionLabel = (category) => {
  const labels = {
    awaiting_review: "need review",
    overdue: "overdue",
    reopened: "reopened",
  };
  return labels[category] || String(category || "").replaceAll("_", " ");
};

const KPI_DRILLDOWN_LABELS = {
  total: "Total Issues",
  active: "Active Issues",
  open: "Open Issues",
  need_review: "Need Review",
  closed: "Closed Issues",
  overdue: "Overdue Issues",
};

const isOperationalLane = (lane) => {
  const name = (lane?.lane_name || "").toString().trim().toLowerCase();
  return lane?.lane_id !== null && lane?.lane_id !== undefined && Number(lane.lane_id) >= 0 && name !== "leadership / unscoped";
};

const OPERATION_METRIC_LEGEND = [
  { label: "Total", description: "All issues in this reporting scope", dot: "bg-slate-500" },
  { label: "Active", description: "Everything not closed", dot: "bg-orange-500" },
  { label: "Open", description: "Awaiting final field response", dot: "bg-amber-500" },
  { label: "Need Review", description: "Final response awaiting authorized review", dot: "bg-blue-500" },
  { label: "Closed", description: "Authorized review/resolution completed", dot: "bg-emerald-500" },
  { label: "Overdue", description: "Open issue past due", dot: "bg-red-500" },
  { label: "Review Coverage", description: "Completed required reviews / review-required issues", dot: "bg-cyan-500" },
  { label: "Reviewer Share", description: "GF / Supervisor / QA/QC share of completed reviews", dot: "bg-violet-500" },
];

const formatRole = (raw) =>
  (raw || "Team member")
    .toString()
    .replaceAll("_", " ")
    .split(" ")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

const getUserName = (user) => {
  const full = `${user?.first_name || ""} ${user?.last_name || ""}`.trim();
  return full || user?.email || "there";
};

const healthClasses = (health) => {
  if (health === "At Risk") return "border-red-200 bg-red-50 text-red-700";
  if (health === "Needs Attention")
    return "border-amber-200 bg-amber-50 text-amber-700";
  if (health === "Good") return "border-blue-200 bg-blue-50 text-blue-700";
  if (health === "Very Good")
    return "border-emerald-200 bg-emerald-50 text-emerald-700";
  return "border-gray-200 bg-gray-50 text-gray-600";
};

const Kpi = ({ label, value, hint, icon: Icon, onClick }) => {
  const Component = onClick ? "button" : "div";
  return (
    <Component
      type={onClick ? "button" : undefined}
      onClick={onClick}
      className={`w-full rounded-2xl border border-gray-200 bg-white p-4 text-left shadow-sm transition ${onClick ? "hover:-translate-y-0.5 hover:border-orange-200 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-orange-400" : ""}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">{label}</p>
          <p className="mt-2 text-3xl font-bold text-gray-900">{value ?? 0}</p>
          <p className="mt-1 text-xs text-gray-500">{hint}</p>
          {onClick ? <p className="mt-3 text-xs font-semibold text-orange-700">Open performance details →</p> : null}
        </div>
        <div className="rounded-2xl bg-orange-50 p-3 text-orange-600">
          <Icon className="h-5 w-5" />
        </div>
      </div>
    </Component>
  );
};

export default function OperationsManagerDashboardView({ onNavigate }) {
  const { activeProject, currentUser, projectRole } = useProjectContext();
  const projectId = activeProject?.id;
  const isSystemAdmin = (currentUser?.role || "").toString().toLowerCase() === "admin";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedLaneId, setSelectedLaneId] = useState(null);
  const [exceptionFilter, setExceptionFilter] = useState("all");
  const [globalIssueFilter, setGlobalIssueFilter] = useState(null);
  const [exportingExcel, setExportingExcel] = useState(false);

  const load = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    setError("");
    try {
      const res = await supervisorAPI.operationsDashboard(projectId);
      setData(res.data || null);
    } catch (err) {
      setError(
        err?.response?.data?.detail ||
          err?.message ||
          "Failed to load operations performance.",
      );
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    load();
  }, [load]);

  const counts = data?.counts || {};
  const lanes = Array.isArray(data?.lanes) ? data.lanes.filter(isOperationalLane) : [];
  const supervisors = Array.isArray(data?.supervisor_performance)
    ? data.supervisor_performance
    : [];

  const rolePerformance = data?.role_performance || {};
  const laneResponseChain = Array.isArray(data?.lane_response_chain)
    ? data.lane_response_chain.filter(isOperationalLane)
    : [];
  const drilldownIssues = Array.isArray(data?.drilldown_issues)
    ? data.drilldown_issues.filter((issue) => isOperationalLane(issue))
    : [];

  const roleResponseCards = useMemo(() => {
    const avg = (rows, getter, eligible = () => true) => {
      const values = rows
        .filter(eligible)
        .map(getter)
        .filter(hasMetric)
        .map(Number);
      return values.length
        ? values.reduce((sum, value) => sum + value, 0) / values.length
        : null;
    };
    const eligibleCount = (rows, eligible) => rows.filter(eligible).length;
    const foremen = Array.isArray(rolePerformance.foremen) ? rolePerformance.foremen : [];
    const gfs = Array.isArray(rolePerformance.general_foremen) ? rolePerformance.general_foremen : [];
    const pes = Array.isArray(rolePerformance.project_engineers) ? rolePerformance.project_engineers : [];
    const sups = Array.isArray(rolePerformance.supervisors) ? rolePerformance.supervisors : [];

    const foremanRateEligible = (r) => Number(r.total || 0) > 0;
    const foremanTimeEligible = (r) => Number(r.first_response_samples || r.responded || 0) > 0;
    const gfRateEligible = (r) => Number(r.escalation_response?.received || 0) > 0;
    const gfTimeEligible = (r) => Number(r.escalation_response?.answered || 0) > 0;
    const peRateEligible = (r) => Number(r.escalation_response?.received || 0) > 0;
    const peTimeEligible = (r) => Number(r.escalation_response?.answered || 0) > 0;
    const supRateEligible = (r) => Number(r.escalation_response?.received || 0) > 0;
    const supTimeEligible = (r) => Number(r.escalation_response?.answered || 0) > 0;

    return [
      {
        label: "Foremen",
        people: foremen.length,
        rate: avg(foremen, (r) => r.response_rate, foremanRateEligible),
        rateSamples: eligibleCount(foremen, foremanRateEligible),
        hours: avg(foremen, (r) => r.median_first_response_hours, foremanTimeEligible),
        hourSamples: eligibleCount(foremen, foremanTimeEligible),
        metric: "first response",
      },
      {
        label: "General Foremen",
        people: gfs.length,
        rate: avg(gfs, (r) => r.escalation_response?.response_rate, gfRateEligible),
        rateSamples: eligibleCount(gfs, gfRateEligible),
        hours: avg(gfs, (r) => r.escalation_response?.median_response_hours, gfTimeEligible),
        hourSamples: eligibleCount(gfs, gfTimeEligible),
        metric: "escalation response",
      },
      {
        label: "Project Engineers",
        people: pes.length,
        rate: avg(pes, (r) => r.escalation_response?.response_rate, peRateEligible),
        rateSamples: eligibleCount(pes, peRateEligible),
        hours: avg(pes, (r) => r.escalation_response?.median_response_hours, peTimeEligible),
        hourSamples: eligibleCount(pes, peTimeEligible),
        metric: "engineering escalation response",
      },
      {
        label: "Supervisors",
        people: sups.length,
        rate: avg(sups, (r) => r.escalation_response?.response_rate, supRateEligible),
        rateSamples: eligibleCount(sups, supRateEligible),
        hours: avg(sups, (r) => r.escalation_response?.median_response_hours, supTimeEligible),
        hourSamples: eligibleCount(sups, supTimeEligible),
        metric: "escalation response",
      },
    ];
  }, [rolePerformance]);

  const riskLanes = useMemo(
    () =>
      lanes.filter((lane) =>
        ["At Risk", "Needs Attention"].includes(lane.health),
      ),
    [lanes],
  );

  const selectedLane = useMemo(
    () => lanes.find((lane) => Number(lane.lane_id) === Number(selectedLaneId)) || null,
    [lanes, selectedLaneId],
  );

  const selectedExceptions = useMemo(() => {
    const rows = Array.isArray(selectedLane?.exception_issues)
      ? selectedLane.exception_issues
      : [];
    if (exceptionFilter === "all") return rows;
    return rows.filter((row) => (row.categories || []).includes(exceptionFilter));
  }, [selectedLane, exceptionFilter]);

  const globalIssueRows = useMemo(() => {
    if (!globalIssueFilter) return [];
    return drilldownIssues.filter((issue) =>
      (issue.metric_keys || []).includes(globalIssueFilter),
    );
  }, [drilldownIssues, globalIssueFilter]);

  const openLaneExceptions = (laneId, category = "all") => {
    setGlobalIssueFilter(null);
    setSelectedLaneId(laneId);
    setExceptionFilter(category);
    window.setTimeout(() => {
      document.getElementById("operations-exception-drilldown")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 0);
  };

  const openGlobalIssues = (metric) => {
    setSelectedLaneId(null);
    setGlobalIssueFilter(metric);
    window.setTimeout(() => {
      document.getElementById("operations-global-drilldown")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 0);
  };

  const scrollToRolePerformance = () =>
    document.getElementById("operations-role-performance")?.scrollIntoView({ behavior: "smooth", block: "start" });

  const exportCsv = () => {
    const headers = [
      "Lane",
      "Supervisor",
      "Health",
      "Total",
      "Active",
      "Open",
      "Need Review",
      "Closed",
      "Overdue",
      "Review Coverage %",
      "Average Review Hours",
      "Median Review Hours",
      "P90 Review Hours",
      "GF Review Share %",
      "Supervisor Review Share %",
      "QA/QC Review Share %",
      "Response Rate %",
      "Median First Response Hours",
      "P90 First Response Hours",
      "Median Resolution Hours",
      "P90 Resolution Hours",
      "Average Open Age Days",
      "Reopen Rate %",
      "Reassignment Rate %",
      "Escalation Rate %",
    ];
    const rows = lanes.map((lane) => [
      lane.lane_name,
      (lane.supervisors || []).map((s) => s.name).join("; "),
      lane.health,
      lane.total,
      lane.active,
      lane.open,
      lane.need_review ?? lane.waiting_review,
      lane.closed ?? lane.completed,
      lane.overdue,
      lane.review_coverage,
      lane.avg_review_hours,
      lane.median_review_hours,
      lane.p90_review_hours,
      lane.general_foreman_review_share,
      lane.supervisor_review_share,
      lane.qa_qc_review_share,
      lane.response_rate,
      lane.median_first_response_hours,
      lane.p90_first_response_hours,
      lane.median_resolution_hours,
      lane.p90_resolution_hours,
      lane.avg_open_age_days,
      lane.reopen_rate,
      lane.reassignment_rate,
      lane.escalation_rate,
    ]);
    const escape = (v) => `"${String(v ?? "").replaceAll('"', '""')}"`;
    const csv = [headers, ...rows].map((row) => row.map(escape).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${activeProject?.name || "project"}-lane-performance.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const exportManagementExcel = async () => {
    if (!projectId) return;
    setExportingExcel(true);
    setError("");
    try {
      const res = await supervisorAPI.operationsManagementReport(projectId);
      const blob = new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${activeProject?.name || "project"}-project-operations-executive-brief.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || "Failed to export management Excel report.");
    } finally {
      setExportingExcel(false);
    }
  };

  if (!activeProject) return null;

  return (
    <div className="space-y-6">
      <div className="rounded-3xl border border-orange-100 bg-gradient-to-br from-orange-50 via-white to-slate-50 p-6 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-1 text-xs font-semibold text-orange-700 ring-1 ring-orange-100">
              <BarChart3 className="h-3.5 w-3.5" /> Field Operations Oversight
            </div>
            <h2 className="mt-3 text-2xl font-bold text-gray-900">Welcome back, {getUserName(currentUser)}</h2>
            <div className="mt-2 flex flex-wrap items-center gap-2 text-sm text-gray-600">
              <span className="rounded-full border border-gray-200 bg-white px-3 py-1 font-medium text-gray-800">Project: {activeProject.name}</span>
              <span className="rounded-full border border-orange-100 bg-orange-50 px-3 py-1 font-medium text-orange-800">Role: {formatRole(projectRole)}</span>
            </div>
            <p className="mt-3 max-w-3xl text-sm text-gray-600">
              Exception-first lane and Supervisor oversight. Focus on workflow status, review participation,
              overdue work, and branch health instead of reading every issue.
            </p>
            <p className="mt-2 text-xs text-gray-500">Organization chart version {data?.chart_version || 0}</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={exportManagementExcel}
              disabled={exportingExcel || !lanes.length}
              className="inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-3 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
            >
              <FileSpreadsheet className="h-4 w-4" /> {exportingExcel ? "Preparing Excel…" : "Export Executive Brief (Excel)"}
            </button>
            <button
              type="button"
              onClick={exportCsv}
              disabled={!lanes.length}
              className="inline-flex items-center gap-2 rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm hover:bg-gray-50 disabled:opacity-50"
            >
              <Download className="h-4 w-4" /> Export Lane Report
            </button>
            <button
              type="button"
              onClick={load}
              disabled={loading}
              className="inline-flex items-center gap-2 rounded-xl bg-gray-900 px-3 py-2 text-sm font-semibold text-white hover:bg-black disabled:opacity-60"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Refresh
            </button>
          </div>
        </div>
      </div>

      {error ? (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">{error}</div>
      ) : null}

      {loading && !data ? (
        <div className="flex items-center justify-center rounded-2xl border p-12"><LoadingSpinner /></div>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-6">
            <Kpi label="Total Issues" value={counts.total_issues} hint="All issues in this reporting scope" icon={BarChart3} onClick={() => openGlobalIssues("total")} />
            <Kpi label="Active" value={counts.active_issues} hint="Everything not closed" icon={TrendingUp} onClick={() => openGlobalIssues("active")} />
            <Kpi label="Open" value={counts.open_issues} hint="Awaiting final field response" icon={Clock3} onClick={() => openGlobalIssues("open")} />
            <Kpi label="Need Review" value={counts.need_review ?? counts.awaiting_review ?? 0} hint="Final response awaiting authorized review" icon={ShieldCheck} onClick={() => openGlobalIssues("need_review")} />
            <Kpi label="Closed" value={counts.closed_issues} hint="Authorized resolution/review completed" icon={ShieldCheck} onClick={() => openGlobalIssues("closed")} />
            <Kpi label="Overdue" value={counts.overdue_issues} hint="Open issue past due" icon={AlertTriangle} onClick={() => openGlobalIssues("overdue")} />
          </div>

          {globalIssueFilter ? (
            <div id="operations-global-drilldown" className="scroll-mt-4 overflow-hidden rounded-2xl border border-orange-200 bg-white shadow-sm">
              <div className="flex flex-col gap-2 border-b px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h3 className="font-semibold text-gray-900">{KPI_DRILLDOWN_LABELS[globalIssueFilter]} — Complete Reporting Scope</h3>
                  <p className="text-xs text-gray-500">All matching issues across every published lane in this dashboard scope.</p>
                </div>
                <button
                  type="button"
                  onClick={() => setGlobalIssueFilter(null)}
                  className="rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-semibold text-gray-600 hover:bg-gray-50"
                >
                  Close
                </button>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
                    <tr>
                      <th className="px-4 py-3">Lane</th>
                      <th className="px-4 py-3">Issue</th>
                      <th className="px-4 py-3">Responsible</th>
                      <th className="px-4 py-3">Status</th>
                      <th className="px-4 py-3">Due</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {globalIssueRows.length === 0 ? (
                      <tr><td colSpan={5} className="px-4 py-8 text-center text-gray-500">No issues match this KPI in the current reporting scope.</td></tr>
                    ) : globalIssueRows.map((issue) => (
                      <tr key={issue.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 font-medium text-gray-900">{issue.lane_name || "—"}</td>
                        <td className="px-4 py-3">
                          <div className="font-semibold text-gray-900">{issue.finding_number || `Issue #${issue.id}`}</div>
                          <div className="mt-0.5 max-w-md truncate text-xs text-gray-500">{issue.description || "—"}</div>
                        </td>
                        <td className="px-4 py-3 text-gray-700">{issue.responsible_user_name || "—"}</td>
                        <td className="px-4 py-3 text-gray-700">{issue.status || "—"}</td>
                        <td className="px-4 py-3 text-gray-700">{issue.due_date || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}

          <div id="operations-review-attribution" className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <h3 className="font-semibold text-gray-900">Review Performance & Attribution</h3>
                <p className="text-xs text-gray-500">Overall review completion plus the role share of completed resolving reviews.</p>
              </div>
              <span className="text-xs text-gray-500">{counts.reviewed || 0} completed reviews</span>
            </div>
            <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-5">
              {[
                ["Review Coverage", Number(counts.review_required || 0) > 0 ? pct(counts.review_coverage) : "N/A", `${counts.reviewed || 0} of ${counts.review_required || 0} required`],
                ["Avg Review Time", hoursText(counts.avg_review_hours, counts.review_samples), `${counts.review_samples || 0} timing samples`],
                ["GF Share", Number(counts.reviewed || 0) > 0 ? pct(counts.general_foreman_review_share) : "N/A", `${counts.reviews_by_general_foreman || 0} of ${counts.reviewed || 0}`],
                ["Supervisor Share", Number(counts.reviewed || 0) > 0 ? pct(counts.supervisor_review_share) : "N/A", `${counts.reviews_by_supervisor || 0} of ${counts.reviewed || 0}`],
                ["QA/QC Share", Number(counts.reviewed || 0) > 0 ? pct(counts.qa_qc_review_share) : "N/A", `${counts.reviews_by_qa_qc || 0} of ${counts.reviewed || 0}`],
              ].map(([label, value, detail]) => (
                <div key={label} className="rounded-xl border border-gray-100 bg-gray-50 p-3">
                  <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">{label}</div>
                  <div className="mt-1 text-xl font-bold text-gray-900">{value}</div>
                  <div className="mt-1 text-xs text-gray-500">{detail}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-gradient-to-r from-slate-50 via-white to-orange-50/60 px-3 py-2 shadow-sm">
            <div className="flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-900 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.16em] text-white">
                Metric legend
              </span>
              {OPERATION_METRIC_LEGEND.map((item) => (
                <span
                  key={item.label}
                  title={`${item.label}: ${item.description}`}
                  className="inline-flex min-w-0 items-center gap-1.5 rounded-full border border-white bg-white px-2.5 py-1 text-[11px] text-gray-600 shadow-sm ring-1 ring-slate-100"
                >
                  <span className={`h-2 w-2 shrink-0 rounded-full ${item.dot}`} />
                  <strong className="whitespace-nowrap text-gray-900">{item.label}</strong>
                  <span className="hidden whitespace-nowrap text-gray-500 2xl:inline">· {item.description}</span>
                </span>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
            <div id="operations-role-performance" className="scroll-mt-4 rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
              <div className="flex items-center gap-2">
                <Activity className="h-5 w-5 text-orange-600" />
                <div>
                  <h3 className="font-semibold text-gray-900">Role Response Analytics</h3>
                  <p className="text-xs text-gray-500">Response rate and median turnaround by field responsibility layer.</p>
                </div>
              </div>
              <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
                {roleResponseCards.map((card) => (
                  <div key={card.label} className="rounded-2xl border border-gray-100 bg-gradient-to-br from-gray-50 to-white p-4">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <p className="text-sm font-semibold text-gray-900">{card.label}</p>
                        <p className="text-[11px] text-gray-500">{card.people} active · {card.metric}</p>
                      </div>
                      <span className="rounded-full bg-orange-50 px-2 py-1 text-xs font-bold text-orange-700">{Number(card.rateSamples || 0) > 0 ? pct(card.rate) : "N/A"}</span>
                    </div>
                    <div className="mt-3">
                      <div className="h-2 overflow-hidden rounded-full bg-gray-100">
                        <div className="h-full rounded-full bg-orange-500" style={{ width: `${hasMetric(card.rate) ? Math.min(100, Number(card.rate)) : 0}%` }} />
                      </div>
                      <p className="mt-2 text-xs text-gray-600">Median turnaround: <strong>{hoursText(card.hours, card.hourSamples)}</strong></p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
              <div className="flex items-center gap-2">
                <Clock3 className="h-5 w-5 text-blue-600" />
                <div>
                  <h3 className="font-semibold text-gray-900">Lane Response-Time Chain</h3>
                  <p className="text-xs text-gray-500">Side-by-side median turnaround helps expose the bottleneck layer.</p>
                </div>
              </div>
              <div className="mt-4 space-y-3">
                {laneResponseChain.length === 0 ? (
                  <div className="rounded-xl border border-dashed p-4 text-sm text-gray-500">No lane response timing data yet.</div>
                ) : laneResponseChain.map((lane) => {
                  const values = [
                    {
                      label: "Foreman",
                      value: lane.foreman_first_response?.median_hours,
                      samples: lane.foreman_first_response?.count,
                    },
                    {
                      label: "GF",
                      value: lane.general_foreman_escalation_response?.median_response_hours,
                      samples: lane.general_foreman_escalation_response?.answered,
                    },
                    {
                      label: "PE",
                      value: lane.project_engineer_escalation_response?.median_response_hours,
                      samples: lane.project_engineer_escalation_response?.answered,
                    },
                    {
                      label: "Sup escalation",
                      value: lane.supervisor_escalation_response?.median_response_hours,
                      samples: lane.supervisor_escalation_response?.answered,
                    },
                    {
                      label: "Review",
                      value: (lane.general_foreman_review ?? lane.supervisor_review)?.median_hours,
                      samples: (lane.general_foreman_review ?? lane.supervisor_review)?.count,
                    },
                    {
                      label: "Resolution",
                      value: lane.total_resolution?.median_hours,
                      samples: lane.total_resolution?.count,
                    },
                  ];
                  const measuredValues = values
                    .filter((item) => Number(item.samples || 0) > 0)
                    .map((item) => Number(item.value || 0));
                  const max = Math.max(1, ...measuredValues);
                  return (
                    <div key={lane.lane_id} className="rounded-xl border border-gray-100 bg-gray-50/60 p-3">
                      <p className="text-sm font-semibold text-gray-900">{lane.lane_name}</p>
                      <div className="mt-2 space-y-1.5">
                        {values.map(({ label, value, samples }) => {
                          const hasSamples = Number(samples || 0) > 0;
                          return (
                            <div key={label} className="grid grid-cols-[100px_1fr_56px] items-center gap-2 text-[11px]">
                              <span className="text-gray-600">{label}</span>
                              <div className="h-2 overflow-hidden rounded-full bg-white">
                                <div className="h-full rounded-full bg-blue-500" style={{ width: `${hasSamples && Number(value || 0) > 0 ? Math.max(2, (Number(value || 0) / max) * 100) : 0}%` }} />
                              </div>
                              <span className="text-right font-semibold text-gray-800">{hoursText(value, samples)}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {riskLanes.length > 0 ? (
            <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4">
              <div className="flex items-center gap-2 text-sm font-semibold text-amber-800">
                <AlertTriangle className="h-4 w-4" /> Attention needed in {riskLanes.length} lane{riskLanes.length === 1 ? "" : "s"}
              </div>
              <div className="mt-2 flex flex-wrap gap-2">
                {riskLanes.map((lane) => (
                  <button
                    key={lane.lane_id}
                    type="button"
                    onClick={() => openLaneExceptions(lane.lane_id)}
                    className="rounded-full border border-amber-200 bg-white px-3 py-1 text-xs text-amber-800 hover:bg-amber-100"
                  >
                    {lane.lane_name}: {lane.health}
                  </button>
                ))}
              </div>
            </div>
          ) : null}

          <div id="operations-lane-health" className="scroll-mt-4 rounded-2xl border border-gray-200 bg-white shadow-sm">
            <div className="border-b px-4 py-3">
              <h3 className="font-semibold text-gray-900">Lane Health</h3>
              <p className="text-xs text-gray-500">Canonical workflow counts use the same definitions as Issues Control. Total = Active + Closed.</p>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
                  <tr>
                    <th className="px-4 py-3">Lane</th><th className="px-4 py-3">Supervisor</th><th className="px-4 py-3">Total</th>
                    <th className="px-4 py-3">Active</th><th className="px-4 py-3">Open</th><th className="px-4 py-3">Need Review</th>
                    <th className="px-4 py-3">Closed</th><th className="px-4 py-3">Overdue</th><th className="px-4 py-3">Review Coverage</th>
                    <th className="px-4 py-3">GF Share</th><th className="px-4 py-3">Supervisor Share</th><th className="px-4 py-3">QA/QC Share</th>
                    <th className="px-4 py-3">Health</th><th className="px-4 py-3">Drill Down</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {lanes.length === 0 ? (
                    <tr><td colSpan={14} className="px-4 py-8 text-center text-gray-500">No organization lanes are published yet.</td></tr>
                  ) : lanes.map((lane) => (
                    <tr key={lane.lane_id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-semibold text-gray-900">{lane.lane_name}</td>
                      <td className="px-4 py-3 text-gray-700">{(lane.supervisors || []).map((s) => s.name).join(", ") || "—"}</td>
                      <td className="px-4 py-3">{lane.total ?? 0}</td>
                      <td className="px-4 py-3">{lane.active ?? Math.max(0, Number(lane.total || 0) - Number(lane.closed || 0))}</td>
                      <td className="px-4 py-3">{lane.open ?? 0}</td>
                      <td className="px-4 py-3">{lane.need_review ?? lane.waiting_review ?? 0}</td>
                      <td className="px-4 py-3">{lane.closed ?? lane.completed ?? 0}</td>
                      <td className="px-4 py-3">{lane.overdue ?? 0}</td>
                      <td className="px-4 py-3">{Number(lane.review_required || 0) > 0 ? pct(lane.review_coverage) : "N/A"}</td>
                      <td className="px-4 py-3">{Number(lane.reviewed || 0) > 0 ? pct(lane.general_foreman_review_share) : "N/A"}</td>
                      <td className="px-4 py-3">{Number(lane.reviewed || 0) > 0 ? pct(lane.supervisor_review_share) : "N/A"}</td>
                      <td className="px-4 py-3">{Number(lane.reviewed || 0) > 0 ? pct(lane.qa_qc_review_share) : "N/A"}</td>
                      <td className="px-4 py-3">
                        <div className="flex flex-col items-start gap-1">
                          <span className={`rounded-full border px-2 py-1 text-xs font-semibold ${healthClasses(lane.health)}`}>{lane.health}</span>
                          {Number(lane.total || 0) > 0 && lane.limited_sample ? <span className="text-[10px] text-gray-500">Limited sample</span> : null}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <button
                          type="button"
                          onClick={() => openLaneExceptions(lane.lane_id)}
                          disabled={!(lane.exception_issues || []).length}
                          className="inline-flex items-center gap-1 rounded-lg border border-gray-300 px-2 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-40"
                        >
                          <Eye className="h-3.5 w-3.5" /> Exceptions
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {selectedLane ? (
            <div id="operations-exception-drilldown" className="scroll-mt-4 rounded-2xl border border-orange-200 bg-white shadow-sm">
              <div className="flex flex-col gap-3 border-b px-4 py-3 lg:flex-row lg:items-center lg:justify-between">
                <div>
                  <h3 className="font-semibold text-gray-900">{selectedLane.lane_name} — Exception Drill-down</h3>
                  <p className="text-xs text-gray-500">Open only the problem categories that need management attention.</p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {[
                    ["all", "All Exceptions"],
                    ["overdue", "Overdue"],
                    ["awaiting_review", "Need Review"],
                    ["reopened", "Reopened"],
                  ].map(([value, label]) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => setExceptionFilter(value)}
                      className={`rounded-full border px-3 py-1 text-xs font-semibold ${exceptionFilter === value ? "border-orange-300 bg-orange-50 text-orange-700" : "border-gray-200 bg-white text-gray-600 hover:bg-gray-50"}`}
                    >
                      {label}
                    </button>
                  ))}
                  <button type="button" onClick={() => setSelectedLaneId(null)} className="rounded-lg border border-gray-300 px-2 py-1 text-xs text-gray-600 hover:bg-gray-50">Close</button>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
                    <tr>
                      <th className="px-4 py-3">Issue</th>
                      <th className="px-4 py-3">Responsible</th>
                      <th className="px-4 py-3">Status</th>
                      <th className="px-4 py-3">Due</th>
                      <th className="px-4 py-3">Exception</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {selectedExceptions.length === 0 ? (
                      <tr><td colSpan={5} className="px-4 py-8 text-center text-gray-500">No issues match this exception filter.</td></tr>
                    ) : selectedExceptions.map((issue) => (
                      <tr key={issue.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <div className="font-semibold text-gray-900">{issue.finding_number || `Issue #${issue.id}`}</div>
                          <div className="mt-0.5 max-w-md truncate text-xs text-gray-500">{issue.description || "—"}</div>
                        </td>
                        <td className="px-4 py-3 text-gray-700">{issue.responsible_user_name || "—"}</td>
                        <td className="px-4 py-3 text-gray-700">{issue.status || "—"}</td>
                        <td className="px-4 py-3 text-gray-700">{issue.due_date || "—"}</td>
                        <td className="px-4 py-3">
                          <div className="flex flex-wrap gap-1">
                            {(issue.categories || []).map((category) => (
                              <span key={category} className="rounded-full bg-gray-100 px-2 py-1 text-[11px] font-medium text-gray-700">{exceptionLabel(category)}</span>
                            ))}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}

          <div id="operations-supervisor-performance" className="scroll-mt-4 rounded-2xl border border-gray-200 bg-white shadow-sm">
            <div className="border-b px-4 py-3">
              <h3 className="font-semibold text-gray-900">Supervisor Escalation Response</h3>
              <p className="text-xs text-gray-500">Response coverage and working-hour turnaround for issues escalated from General Foremen or Project Engineers.</p>
            </div>
            <div className="grid grid-cols-1 gap-3 p-4 lg:grid-cols-2 xl:grid-cols-3">
              {supervisors.map((row, index) => (
                <div key={`${row.supervisor_user_id}-${index}`} className="rounded-2xl border border-gray-200 p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="font-semibold text-gray-900">{row.supervisor_name || "Unassigned Supervisor"}</p>
                      <p className="text-xs text-gray-500">{(row.lane_names || []).join(" • ") || "No lanes assigned"}</p>
                    </div>
                    <span className="rounded-full bg-gray-100 px-2 py-1 text-xs font-semibold text-gray-700">{Number(row.escalation_response?.received || 0) > 0 ? pct(row.escalation_response?.response_rate) : "N/A"}</span>
                  </div>
                  <div className="mt-4 grid grid-cols-2 gap-2 text-xs">
                    <div className="rounded-lg bg-gray-50 p-2"><span className="text-gray-500">Received</span><div className="text-lg font-bold text-gray-900">{row.escalation_response?.received || 0}</div></div>
                    <div className="rounded-lg bg-gray-50 p-2"><span className="text-gray-500">Open</span><div className="text-lg font-bold text-gray-900">{row.escalation_response?.open || 0}</div></div>
                    <div className="rounded-lg bg-gray-50 p-2"><span className="text-gray-500">Answered</span><div className="text-lg font-bold text-gray-900">{row.escalation_response?.answered || 0}</div></div>
                    {isSystemAdmin ? (
                      <div className="rounded-lg bg-gray-50 p-2"><span className="text-gray-500">Median response</span><div className="text-lg font-bold text-gray-900">{hoursText(row.escalation_response?.median_response_hours, row.escalation_response?.answered)}</div></div>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
